"use client";

import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Logo } from "./logo";
import { Briefcase, ListTodo, MenuIcon, PlusIcon, WalletIcon, Wand2, Zap } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@super-resume-new/ui/components/button";
import { Group, GroupSeparator } from "@super-resume-new/ui/components/group";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "@super-resume-new/ui/components/menu";
import { Skeleton } from "@super-resume-new/ui/components/skeleton";

import { getCredits } from "@/functions/get-credits";
import { authClient } from "@/lib/auth-client";
import { cn } from "@super-resume-new/ui/lib/utils";
import UserMenu from "./user-menu";

function NavCredits() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const { data: credits, isLoading: creditsLoading } = useQuery({
    queryKey: ["credits"],
    queryFn: () => getCredits(),
    enabled: !!session,
    staleTime: 0,
  });
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted || sessionPending || (session && creditsLoading)) {
    return <Skeleton className="h-8 w-28 rounded-full" />;
  }

  if (!session) return null;

  return (
    <Group>
      <Button type="button" variant="outline" onClick={() => navigate({ to: "/credits" })}>
        <WalletIcon />
        <span className="font-semibold tabular-nums">{credits?.balance ?? 0}</span>
      </Button>
      <GroupSeparator />
      <Button
        variant="outline"
        size="icon"
        aria-label="Buy credits"
        onClick={() => navigate({ to: "/credits" })}
      >
        <PlusIcon />
      </Button>
    </Group>
  );
}

const NAV_LINKS = [
  { to: "/resume-tailor", label: "Resume Tailor", icon: Wand2 },
  { to: "/ats-booster", label: "ATS Booster", icon: Zap },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/job-tracker", label: "Tracker", icon: ListTodo },
] as const;

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (isHomePage) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition duration-300",
        isHomePage || isScrolled
          ? "border-b border-border bg-background/80 backdrop-blur-sm"
          : "border-b border-transparent bg-transparent",
        isScrolled && "shadow-xs",
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
        <Link
          to="/"
          className="flex items-center gap-2 font-semibold text-foreground hover:text-foreground mr-2"
        >
          <Logo className="size-6 rounded-sm" />
          <span>JobCues</span>
        </Link>

        <div className="flex-1" />

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to}>
              <Button variant="ghost" size="sm" className="font-medium">
                <Icon className="size-3.5 text-muted-foreground" aria-hidden="true" />
                {label}
              </Button>
            </Link>
          ))}
        </nav>

        <NavCredits />
        <UserMenu />

        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon" aria-label="Open menu" className="md:hidden" />
            }
          >
            <MenuIcon className="size-4" />
          </MenuTrigger>
          <MenuPopup align="end" sideOffset={8} className="w-52 md:hidden">
            {NAV_LINKS.map(({ to, label, icon: Icon }) => (
              <MenuItem key={to} render={<Link to={to} />}>
                <Icon className="size-4" aria-hidden="true" />
                {label}
              </MenuItem>
            ))}
          </MenuPopup>
        </Menu>
      </div>
    </header>
  );
}
